import React, { createContext, useContext, useState, useCallback } from 'react';

export interface SubNavItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  to: string;
  section?: string;
}

export interface SubNavLocation {
  pathname: string;
  search: unknown;
}

export interface SubNavConfig {
  title: string;
  basePath: string;
  items: SubNavItem[];
  getActiveId?: (location: SubNavLocation) => string | undefined;
  /** Enable built-in search filtering in the SubNav. */
  searchable?: boolean;
  /** Placeholder text for the SubNav search input. */
  searchPlaceholder?: string;
}

interface SubNavContextValue {
  config: SubNavConfig | null;
  setConfig: (config: SubNavConfig | null) => void;
  isSubNavActive: boolean;
}

const SubNavContext = createContext<SubNavContextValue | null>(null);

export function SubNavProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfigState] = useState<SubNavConfig | null>(null);

  const setConfig = useCallback((newConfig: SubNavConfig | null) => {
    setConfigState(newConfig);
  }, []);

  const value: SubNavContextValue = {
    config,
    setConfig,
    isSubNavActive: config !== null,
  };

  return <SubNavContext.Provider value={value}>{children}</SubNavContext.Provider>;
}

export function useSubNavContext(): SubNavContextValue {
  const context = useContext(SubNavContext);
  if (!context) {
    throw new Error('useSubNavContext must be used within a SubNavProvider');
  }
  return context;
}
